import { useState } from "react";
import { yupResolver } from "@hookform/resolvers/yup";
import { Button, Col, Form, Row } from "react-bootstrap";
import { useFieldArray, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import {
  DropdownFilter,
  FloatInput,
  FloatNumericInput,
  FloatSelect,
  showToast,
  WizardFooter,
} from "src/componentes";
import { ConsultarPessoa } from "src/hook/Consulta/ConsultaPessoa.Module";
import { PessoaDto } from "src/pages/Privada/Entidade/Pessoa/Pessoa.Dto";
import PessoaCadastroView from "src/pages/Privada/Entidade/Pessoa/Cadastro/PessoaCadastro.View";
import {
  ETipoCarroceriaMDFe,
  ETipoRodadoMDFe,
  ETipoProprietarioMDFe,
  ETipoTransportadorMDFe,
  getLocal,
  MDFeInput,
  STORAGEMDFE_KEY,
  tipoCarroceriaMDFeOptions,
  tipoProprietarioMDFeOptions,
  tipoRodadoMDFeOptions,
  tipoTransportadorMDFeOptions,
  ufOptions,
  updateLocal,
} from "../MDFe.Dto";
import { VeiculoFormType, VeiculoSchema } from "./MDFe.Cadastro.Dto";
import TipoTransportadorAjuda from "./Componentes/TipoTransportadorAjuda";

const MDFeCadastroVeiculoView = () => {
  const navigate = useNavigate();
  const mdfe = getLocal<MDFeInput>(STORAGEMDFE_KEY);
  const pessoaBuscar = ConsultarPessoa();
  const [cadastroIndex, setCadastroIndex] = useState<number | null>(null);

  const [possuiProprietarioTerceiro, setPossuiProprietarioTerceiro] = useState<boolean>(
    Boolean(
      mdfe?.proprietarioVeiculoTracao &&
      (mdfe.proprietarioVeiculoTracao.nome ||
        mdfe.proprietarioVeiculoTracao.documento ||
        mdfe.proprietarioVeiculoTracao.cpf ||
        mdfe.proprietarioVeiculoTracao.cnpj)
    )
  );

  const form = useForm<VeiculoFormType>({
    resolver: yupResolver(VeiculoSchema),
    defaultValues: {
      placaVeiculoTracao: mdfe?.placaVeiculoTracao ?? "",
      ufVeiculoTracao: mdfe?.ufVeiculoTracao || mdfe?.ufInicio || "RS",
      renavamVeiculoTracao: mdfe?.renavamVeiculoTracao ?? "",
      taraVeiculoTracao: mdfe?.taraVeiculoTracao ?? 15000,
      capacidadeKGVeiculoTracao: mdfe?.capacidadeKGVeiculoTracao ?? undefined,
      capacidadeM3VeiculoTracao: mdfe?.capacidadeM3VeiculoTracao ?? undefined,
      tipoRodadoVeiculoTracao:
        mdfe?.tipoRodadoVeiculoTracao ?? ETipoRodadoMDFe.Truck,
      tipoCarroceriaVeiculoTracao:
        mdfe?.tipoCarroceriaVeiculoTracao ?? ETipoCarroceriaMDFe.Aberta,
      tipoTransportador:
        possuiProprietarioTerceiro && mdfe?.tipoTransportador
          ? mdfe.tipoTransportador
          : undefined,
      proprietarioVeiculoTracao:
        possuiProprietarioTerceiro && mdfe?.proprietarioVeiculoTracao
          ? {
            documento:
              mdfe.proprietarioVeiculoTracao.documento ||
              mdfe.proprietarioVeiculoTracao.cnpj ||
              mdfe.proprietarioVeiculoTracao.cpf ||
              "",
            rntrc: mdfe.proprietarioVeiculoTracao.rntrc ?? "",
            nome: mdfe.proprietarioVeiculoTracao.nome ?? "",
            ie: mdfe.proprietarioVeiculoTracao.ie ?? "",
            uf: mdfe.proprietarioVeiculoTracao.uf ?? (mdfe?.ufVeiculoTracao || mdfe?.ufInicio || "RS"),
            tipoProprietario: mdfe.proprietarioVeiculoTracao.tipoProprietario ?? 0,
          }
          : undefined,
      condutores:
        mdfe?.condutores && mdfe.condutores.length > 0
          ? mdfe.condutores
          : [
            {
              nomeCondutor: "",
              cpfCondutor: "",
            },
          ],
      reboques: mdfe?.reboques && mdfe.reboques.length > 0 ? mdfe.reboques : [],
    },
  });

  const {
    fields: condutoresFields,
    append: appendCondutor,
    remove: removeCondutor,
  } = useFieldArray({
    control: form.control,
    name: "condutores",
  });

  const {
    fields: reboquesFields,
    append: appendReboque,
    remove: removeReboque,
  } = useFieldArray({
    control: form.control,
    name: "reboques",
  });

  const handleVoltar = () => {
    navigate("../percurso");
  };

  const onSubmit = (data: VeiculoFormType) => {
    let proprietarioSanitizado = undefined;
    let tipoTransportadorSanitizado = undefined;

    if (possuiProprietarioTerceiro && data.proprietarioVeiculoTracao) {
      const prop = data.proprietarioVeiculoTracao;
      const docLimpo = prop.documento
        ? prop.documento.replace(/[.\-/\s]/g, "").toUpperCase()
        : "";
      proprietarioSanitizado = {
        documento: docLimpo,
        cpf: docLimpo.length === 11 ? docLimpo : undefined,
        cnpj: docLimpo.length === 14 ? docLimpo : undefined,
        rntrc: prop.rntrc ? String(prop.rntrc).replace(/\D/g, "").slice(0, 8) : "",
        nome: (prop.nome || "").trim().toUpperCase(),
        ie: prop.ie ? prop.ie.replace(/[^A-Za-z0-9]/g, "").toUpperCase() : undefined,
        uf: (prop.uf || "").toUpperCase().trim().slice(0, 2),
        tipoProprietario: Number(prop.tipoProprietario) || 0,
      };
      if (data.tipoTransportador) {
        tipoTransportadorSanitizado = Number(data.tipoTransportador);
      }
    }

    updateLocal<MDFeInput>(STORAGEMDFE_KEY, {
      placaVeiculoTracao: data.placaVeiculoTracao.replace(/[^A-Za-z0-9]/g, "").toUpperCase(),
      ufVeiculoTracao: data.ufVeiculoTracao,
      renavamVeiculoTracao: data.renavamVeiculoTracao || undefined,
      taraVeiculoTracao: Number(data.taraVeiculoTracao),
      capacidadeKGVeiculoTracao: data.capacidadeKGVeiculoTracao
        ? Number(data.capacidadeKGVeiculoTracao)
        : undefined,
      capacidadeM3VeiculoTracao: data.capacidadeM3VeiculoTracao
        ? Number(data.capacidadeM3VeiculoTracao)
        : undefined,
      tipoRodadoVeiculoTracao: Number(data.tipoRodadoVeiculoTracao),
      tipoCarroceriaVeiculoTracao: Number(data.tipoCarroceriaVeiculoTracao),
      proprietarioVeiculoTracao: proprietarioSanitizado,
      tipoTransportador: tipoTransportadorSanitizado,
      condutores: data.condutores.map((c) => ({
        nomeCondutor: c.nomeCondutor.trim(),
        cpfCondutor: c.cpfCondutor.replace(/\D/g, ""),
      })),
      reboques:
        data.reboques && data.reboques.length > 0
          ? data.reboques.map((r) => ({
            codigoInterno: r.codigoInterno || undefined,
            placa: r.placa.replace(/[^A-Za-z0-9]/g, "").toUpperCase(),
            renavam: r.renavam || undefined,
            tara: Number(r.tara),
            capacidadeKG: Number(r.capacidadeKG),
            capacidadeM3: r.capacidadeM3 ? Number(r.capacidadeM3) : undefined,
            tipoCarroceria: Number(r.tipoCarroceria),
            uf: r.uf,
          }))
          : undefined,
    });

    navigate("../carregamento");
  };

  const handlePessoaSuccess = (value: PessoaDto) => {
    if (cadastroIndex !== null) {
      form.setValue(`condutores.${cadastroIndex}.nomeCondutor`, value.nome);
      form.setValue(
        `condutores.${cadastroIndex}.cpfCondutor`,
        value.documento ? value.documento.replace(/\D/g, "") : ""
      );
    }
    setCadastroIndex(null);
  };

  const handlePessoaCancel = () => {
    setCadastroIndex(null);
  };

  if (cadastroIndex !== null) {
    return (
      <Row className="mt-3">
        <PessoaCadastroView
          onSuccess={handlePessoaSuccess}
          onCancel={handlePessoaCancel}
        />
      </Row>
    );
  }

  const onError = (errors: any) => {
    if (errors.proprietarioVeiculoTracao) {
      showToast({
        message:
          "O grupo 'Proprietário do Veículo de Tração' possui campos obrigatórios inválidos ou não preenchidos.",
        title: "Grupo Obrigatório",
        variant: "Warning",
      });
      return;
    }
    if (errors.tipoTransportador) {
      showToast({
        message:
          errors.tipoTransportador.message || "Tipo de transportador inválido.",
        title: "Validação",
        variant: "Warning",
      });
      return;
    }
    if (errors.condutores) {
      showToast({
        message:
          "O grupo 'Condutores do Veículo' é obrigatório. Informe ao menos um condutor com nome e CPF válidos.",
        title: "Grupo Obrigatório",
        variant: "Warning",
      });
      return;
    }
    if (errors.reboques) {
      showToast({
        message:
          "Verifique os dados dos reboques acoplados (placa, UF, tara e capacidade).",
        title: "Grupo Obrigatório",
        variant: "Warning",
      });
      return;
    }

    const firstErrorMessage =
      errors.placaVeiculoTracao?.message ||
      errors.ufVeiculoTracao?.message ||
      errors.taraVeiculoTracao?.message ||
      errors.tipoRodadoVeiculoTracao?.message ||
      errors.tipoCarroceriaVeiculoTracao?.message ||
      errors.renavamVeiculoTracao?.message ||
      errors.capacidadeKGVeiculoTracao?.message ||
      errors.capacidadeM3VeiculoTracao?.message;

    showToast({
      message:
        firstErrorMessage ||
        "Verifique os dados obrigatórios do Veículo de Tração (placa, UF e tara).",
      title: "Validação do Veículo",
      variant: "Warning",
    });
  };

  return (
    <Form noValidate onSubmit={form.handleSubmit(onSubmit, onError)} className="mt-3 mb-5">
      {/* Veículo de Tração */}
      <div className="mb-4">
        <h5 className="mb-3 text-primary">Veículo de Tração</h5>
        <Row className="g-3">
          <Col xs={12} md={4}>
            <FloatInput
              label="Placa do Veículo"
              inputMask="maiusculas"
              registerField={form.register("placaVeiculoTracao")}
              errorsMessage={form.formState.errors.placaVeiculoTracao}
              maxLength={8}
            />
          </Col>

          <Col xs={12} md={4}>
            <FloatSelect
              label="UF do Emplacamento"
              options={ufOptions}
              register={form.register("ufVeiculoTracao")}
              defaultValue={form.getValues("ufVeiculoTracao")}
              errorsMessage={form.formState.errors.ufVeiculoTracao}
            />
          </Col>

          <Col xs={12} md={4}>
            <FloatInput
              label="Renavam (Opcional)"
              registerField={form.register("renavamVeiculoTracao")}
              errorsMessage={form.formState.errors.renavamVeiculoTracao}
              inputMask="int"
              maxLength={11}
            />
          </Col>

          <Col xs={12} md={4}>
            <FloatNumericInput
              label="Tara (KG)"
              name="taraVeiculoTracao"
              control={form.control}
              errorsMessage={form.formState.errors.taraVeiculoTracao}
              decimalScale={0}
            />
          </Col>

          <Col xs={12} md={4}>
            <FloatNumericInput
              label="Capacidade em KG (Opcional)"
              name="capacidadeKGVeiculoTracao"
              control={form.control}
              errorsMessage={form.formState.errors.capacidadeKGVeiculoTracao}
              decimalScale={0}
            />
          </Col>

          <Col xs={12} md={4}>
            <FloatNumericInput
              label="Capacidade em M³ (Opcional)"
              name="capacidadeM3VeiculoTracao"
              control={form.control}
              errorsMessage={form.formState.errors.capacidadeM3VeiculoTracao}
              decimalScale={0}
            />
          </Col>

          <Col xs={12} md={6}>
            <FloatSelect
              label="Tipo de Rodado"
              options={tipoRodadoMDFeOptions}
              register={form.register("tipoRodadoVeiculoTracao", {
                valueAsNumber: true,
              })}
              defaultValue={form.getValues("tipoRodadoVeiculoTracao")}
              errorsMessage={form.formState.errors.tipoRodadoVeiculoTracao}
            />
          </Col>

          <Col xs={12} md={6}>
            <FloatSelect
              label="Tipo de Carroceria"
              options={tipoCarroceriaMDFeOptions}
              register={form.register("tipoCarroceriaVeiculoTracao", {
                valueAsNumber: true,
              })}
              defaultValue={form.getValues("tipoCarroceriaVeiculoTracao")}
              errorsMessage={form.formState.errors.tipoCarroceriaVeiculoTracao}
            />
          </Col>
        </Row>
      </div>

      {/* Proprietário do Veículo de Tração (Veículo de Terceiro) */}
      <div className="mb-4 p-3 border rounded">
        <div className="d-flex justify-content-between align-items-center mb-2">
          <div>
            <h5 className="text-primary mb-1">Proprietário do Veículo de Tração</h5>
            <small className="text-muted">
              Preencha apenas se o veículo de tração pertencer a terceiro (Rejeição 745 SEFAZ)
            </small>
          </div>
          <Form.Check
            type="switch"
            id="switch-proprietario-veiculo"
            label="Veículo pertence a terceiro"
            checked={possuiProprietarioTerceiro}
            onChange={(e) => {
              const checked = e.target.checked;
              setPossuiProprietarioTerceiro(checked);
              if (!checked) {
                form.setValue("proprietarioVeiculoTracao", undefined);
                form.setValue("tipoTransportador", undefined);
              } else {
                form.setValue("proprietarioVeiculoTracao", {
                  documento: "",
                  rntrc: "",
                  nome: "",
                  ie: "",
                  uf: form.getValues("ufVeiculoTracao") || "RS",
                  tipoProprietario: 0,
                });
                form.setValue("tipoTransportador", 1);
              }
            }}
          />
        </div>

        {possuiProprietarioTerceiro && (() => {
          const docPropLimpo = (
            form.watch("proprietarioVeiculoTracao.documento") || ""
          ).replace(/[.\-/\s]/g, "");
          const isPJProprietario = docPropLimpo.length === 14;

          return (
            <div className="mt-3">
              <Row className="g-3">
                <Col xs={12} md={4}>
                  <FloatInput
                    label="CPF / CNPJ do Proprietário *"
                    inputMask="cnpj-cpf"
                    maxLength={18}
                    registerField={form.register("proprietarioVeiculoTracao.documento", {
                      onChange: (e) => {
                        const limpo = (e.target.value || "").replace(/[.\-/\s]/g, "");
                        if (limpo.length === 14) {
                          const currentTipoTransp = form.getValues("tipoTransportador");
                          if (Number(currentTipoTransp) === 2) {
                            form.setValue("tipoTransportador", 1);
                          }
                        }
                      },
                    })}
                    errorsMessage={form.formState.errors.proprietarioVeiculoTracao?.documento}
                  />
                </Col>

                <Col xs={12} md={4}>
                  <FloatInput
                    label="RNTRC do Proprietário (8 dígitos) *"
                    inputMask="int"
                    maxLength={8}
                    registerField={form.register("proprietarioVeiculoTracao.rntrc")}
                    errorsMessage={form.formState.errors.proprietarioVeiculoTracao?.rntrc}
                  />
                </Col>

                <Col xs={12} md={4}>
                  <FloatInput
                    label="Nome / Razão Social do Proprietário *"
                    inputMask="maiusculas"
                    maxLength={60}
                    registerField={form.register("proprietarioVeiculoTracao.nome")}
                    errorsMessage={form.formState.errors.proprietarioVeiculoTracao?.nome}
                  />
                </Col>

                <Col xs={12} md={3}>
                  <FloatSelect
                    label="UF do Proprietário *"
                    options={ufOptions}
                    register={form.register("proprietarioVeiculoTracao.uf")}
                    errorsMessage={form.formState.errors.proprietarioVeiculoTracao?.uf}
                  />
                </Col>

                <Col xs={12} md={3}>
                  <FloatInput
                    label="Inscrição Estadual (Opcional)"
                    inputMask="int"
                    maxLength={14}
                    registerField={form.register("proprietarioVeiculoTracao.ie")}
                    errorsMessage={form.formState.errors.proprietarioVeiculoTracao?.ie}
                  />
                </Col>

                <Col xs={12} md={3}>
                  <FloatSelect
                    label="Tipo de Proprietário *"
                    options={tipoProprietarioMDFeOptions}
                    register={form.register("proprietarioVeiculoTracao.tipoProprietario", {
                      valueAsNumber: true,
                    })}
                    errorsMessage={form.formState.errors.proprietarioVeiculoTracao?.tipoProprietario}
                  />
                </Col>

                <Col xs={12} md={3}>
                  <FloatSelect
                    label="Tipo de Transportador *"
                    options={
                      isPJProprietario
                        ? tipoTransportadorMDFeOptions.filter(
                          (opt) => opt.value !== "2" && opt.value !== ""
                        )
                        : tipoTransportadorMDFeOptions.filter((opt) => opt.value !== "")
                    }
                    register={form.register("tipoTransportador", {
                      valueAsNumber: true,
                    })}
                    errorsMessage={form.formState.errors.tipoTransportador}
                    helpElement={<TipoTransportadorAjuda />}
                  />
                  {isPJProprietario && (
                    <small className="text-muted">
                      Conforme SEFAZ (Rejeição 744), para proprietário PJ é permitido apenas ETC ou CTC.
                    </small>
                  )}
                </Col>
              </Row>
            </div>
          );
        })()}
      </div>

      {/* Condutores */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <h5 className="text-primary mb-0">Condutores (Motoristas)</h5>
          <Button
            variant="outline-primary"
            size="sm"
            onClick={() =>
              appendCondutor({
                nomeCondutor: "",
                cpfCondutor: "",
              })
            }
          >
            + Adicionar Condutor
          </Button>
        </div>

        {condutoresFields.map((field, index) => (
          <div key={field.id} className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-semibold text-secondary">
                Motorista #{index + 1}
              </span>
              {condutoresFields.length > 1 && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={() => removeCondutor(index)}
                >
                  Remover
                </Button>
              )}
            </div>

            <Row className="g-3 align-items-start">
              <Col xs={12} md={7}>
                <DropdownFilter<PessoaDto>
                  fetchData={pessoaBuscar.PessoaBuscar}
                  loading={pessoaBuscar.loading}
                  labelDescription="Nome do Condutor"
                  getDescription={(item: PessoaDto) => item.nome}
                  getId={(item: PessoaDto) => item.id}
                  registeredFields={{
                    id: form.register(`condutores.${index}.nomeCondutor`),
                    description: form.register(
                      `condutores.${index}.nomeCondutor`
                    ),
                  }}
                  errorsMessageDescription={
                    form.formState.errors.condutores?.[index]?.nomeCondutor
                  }
                  onSelect={(item: PessoaDto | undefined) => {
                    if (item) {
                      form.setValue(
                        `condutores.${index}.nomeCondutor`,
                        item.nome
                      );
                      form.setValue(
                        `condutores.${index}.cpfCondutor`,
                        item.documento ? item.documento.replace(/\D/g, "") : ""
                      );
                    }
                  }}
                />
                <div className="mt-1">
                  <Button
                    variant="link"
                    size="sm"
                    className="p-0 text-decoration-none text-muted"
                    onClick={() => setCadastroIndex(index)}
                  >
                    + Cadastrar
                  </Button>
                </div>
              </Col>

              <Col xs={12} md={5}>
                <FloatInput
                  label="CPF do Condutor (11 dígitos)"
                  registerField={form.register(
                    `condutores.${index}.cpfCondutor`
                  )}
                  errorsMessage={
                    form.formState.errors.condutores?.[index]?.cpfCondutor
                  }
                  inputMask="cpf"
                />
              </Col>
            </Row>
          </div>
        ))}
      </div>

      {/* Reboques */}
      <div className="mb-4">
        <div className="d-flex justify-content-between align-items-center mb-3">
          <div>
            <h5 className="text-primary mb-0">Reboques Acoplados</h5>
            <small className="text-muted">Opcional (máximo de 3 reboques)</small>
          </div>
          {reboquesFields.length < 3 && (
            <Button
              variant="outline-primary"
              size="sm"
              onClick={() =>
                appendReboque({
                  placa: "",
                  uf: "RS",
                  tara: 0,
                  tipoCarroceria: ETipoCarroceriaMDFe.Aberta,
                  capacidadeKG: 0,
                  capacidadeM3: undefined,
                  renavam: "",
                })
              }
            >
              + Adicionar Reboque
            </Button>
          )}
        </div>

        {reboquesFields.map((field, index) => (
          <div key={field.id} className="mb-3">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <span className="fw-semibold text-secondary">
                Reboque #{index + 1}
              </span>
              <Button
                variant="outline-danger"
                size="sm"
                onClick={() => removeReboque(index)}
              >
                Remover
              </Button>
            </div>
            <Row className="g-3">
              <Col xs={12} md={3}>
                <FloatInput
                  label="Placa do Reboque"
                  inputMask="maiusculas"
                  registerField={form.register(`reboques.${index}.placa`)}
                  errorsMessage={
                    form.formState.errors.reboques?.[index]?.placa
                  }
                  maxLength={8}
                />
              </Col>
              <Col xs={12} md={3}>
                <FloatSelect
                  label="UF"
                  options={ufOptions}
                  register={form.register(`reboques.${index}.uf`)}
                  defaultValue={form.getValues("reboques")?.[index]?.uf}
                  errorsMessage={form.formState.errors.reboques?.[index]?.uf}
                />
              </Col>
              <Col xs={12} md={3}>
                <FloatNumericInput
                  label="Tara (KG)"
                  name={`reboques.${index}.tara`}
                  control={form.control}
                  errorsMessage={form.formState.errors.reboques?.[index]?.tara}
                  decimalScale={0}
                />
              </Col>
              <Col xs={12} md={3}>
                <FloatSelect
                  label="Carroceria"
                  options={tipoCarroceriaMDFeOptions}
                  register={form.register(
                    `reboques.${index}.tipoCarroceria`,
                    { valueAsNumber: true }
                  )}
                  defaultValue={form.getValues("reboques")?.[index]?.tipoCarroceria}
                  errorsMessage={
                    form.formState.errors.reboques?.[index]?.tipoCarroceria
                  }
                />
              </Col>
              <Col xs={12} md={4}>
                <FloatInput
                  label="Código Interno (Opcional)"
                  registerField={form.register(`reboques.${index}.codigoInterno`)}
                  errorsMessage={
                    form.formState.errors.reboques?.[index]?.codigoInterno
                  }
                  maxLength={10}
                />
              </Col>
              <Col xs={12} md={4}>
                <FloatInput
                  label="Renavam (Opcional)"
                  registerField={form.register(`reboques.${index}.renavam`)}
                  errorsMessage={
                    form.formState.errors.reboques?.[index]?.renavam
                  }
                  inputMask="int"
                  maxLength={11}
                />
              </Col>
              <Col xs={12} md={4}>
                <FloatNumericInput
                  label="Capacidade Carga (KG)"
                  name={`reboques.${index}.capacidadeKG`}
                  control={form.control}
                  errorsMessage={form.formState.errors.reboques?.[index]?.capacidadeKG}
                  decimalScale={0}
                />
              </Col>
            </Row>
          </div>
        ))}
      </div>

      <WizardFooter onVoltar={handleVoltar} />
    </Form>
  );
};

export default MDFeCadastroVeiculoView;
